import wretch from 'wretch';
import { useQuery } from '@tanstack/react-query';
import { subDays, endOfDay, format } from 'date-fns';

import { createOurDate } from '../utils';

// Frankfurter serves the official ECB reference rates ("srednji tečaj ECB-a"),
// no API key required and CORS-enabled, so we can call it directly from the browser.
const FRANKFURTER_URL = 'https://api.frankfurter.dev/v1';
// How far back to fetch so both the "Odaberi tecaj" modal (30d) and best-rate
// lookups always have data, including across weekends/holidays.
const HISTORY_DAYS = 120;

type FrankfurterTimeSeries = {
  amount: number;
  base: string;
  start_date: string;
  end_date: string;
  rates: Record<string, { USD: number }>;
};

export type RateItem = {
  date: string;
  rate: string;
};

const getExchangeRates = async (): Promise<RateItem[]> => {
  const end = new Date();
  const start = subDays(end, HISTORY_DAYS);
  const range = `${format(start, 'yyyy-MM-dd')}..${format(end, 'yyyy-MM-dd')}`;

  const data = await wretch(`${FRANKFURTER_URL}/${range}?base=EUR&symbols=USD`).get().json<FrankfurterTimeSeries>();

  return Object.entries(data.rates)
    .map(([date, { USD }]) => ({ date, rate: String(USD) }))
    .sort((a, b) => b.date.localeCompare(a.date));
};

function useExchange() {
  const {
    isLoading,
    error,
    data: rates,
  } = useQuery({
    queryKey: ['exchange'],
    queryFn: getExchangeRates,
  });

  const getRatesInRange = (datum: string, days: number) => {
    if (isLoading || error) {
      return [];
    }

    const endDate = createOurDate(datum);
    const startDate = subDays(endDate, days);
    const startTs = startDate.getTime();
    const endTs = endOfDay(endDate).getTime();

    const options = [];
    for (const item of rates || []) {
      const ts = new Date(item.date).getTime();
      if (ts >= startTs && ts <= endTs) {
        options.push(item);
      }
    }
    return options;
  };

  const getBestRate = (datum: string, days: number) => {
    const options = getRatesInRange(datum, days);
    if (options.length === 0) {
      return undefined;
    }
    const best = options.reduce(
      (acc, item) => {
        if (parseFloat(item.rate) > parseFloat(acc.rate)) {
          return item;
        }
        return acc;
      },
      { rate: '0', date: '' },
    );
    return best;
  };

  return {
    isLoading,
    error,
    rates,
    getRatesInRange,
    getBestRate,
  };
}

export default useExchange;
